<template>
  <div class="sp-countdown" :class="classModifiers">
    <span class="sp-countdown__label">{{ label }}</span>
    <span class="sp-countdown__clock-container">
      <span class="sp-countdown__clock">{{ days }}:{{ hours }}:{{ minutes }}:{{ seconds }}</span>
      <span class="sp-countdown__clock-label">
        <span>Days</span>
        <span>Hours</span>
        <span>Min<span>utes</span></span>
        <span>Sec<span>onds</span></span>
      </span>
    </span>
  </div>
</template>

<script setup>
import { props as countdownProps } from "./props.js";
import { useNow, watchImmediate } from "@vueuse/core";
import { computed, ref } from "vue";
import { useBreakpoints } from "#ce/composables/useBreakpoints";

const SECONDS_PER_MINUTE = 60;
const SECONDS_PER_HOUR = 60 * SECONDS_PER_MINUTE;
const SECONDS_PER_DAY = 24 * SECONDS_PER_HOUR;

const props = defineProps(countdownProps);

const { breakpointModifiers } = useBreakpoints();
const classModifiers = computed(() => [...breakpointModifiers.value]);

function sanitizeTimezone(timezone) {
  if (!timezone) {
    return "";
  }

  if (timezone.includes(":")) {
    return timezone;
  }

  return `${timezone.slice(0, 3)}:${timezone.slice(3)}`;
}

function parseRailsDateTime(dateString) {
  const parts = dateString.trim().split(/\s+/);

  if (parts.length < 3) {
    return new Date(NaN);
  }

  const [date, time, tz] = parts;

  const timezone = sanitizeTimezone(tz);

  return new Date(`${date}T${time}${timezone}`);
}

const nowDate = useNow();
const now = computed(() => nowDate.value.getTime());
const countdownDate = ref(null);

const remainingSeconds = computed(() => {
  if (!countdownDate.value) {
    return 0;
  }

  const timeDifference = Math.floor((countdownDate.value - now.value) / 1000);
  return Math.max(0, timeDifference);
});

function initializeCountdown() {
  const parsedDate = parseRailsDateTime(props.date);

  if (isNaN(parsedDate.getTime())) {
    countdownDate.value = null;
    return;
  }

  countdownDate.value = parsedDate.getTime();
}

function formatTimeUnit(value) {
  return Math.max(0, value).toString().padStart(2, "0");
}

watchImmediate(() => props.date, initializeCountdown);

const days = computed(() => Math.max(0, Math.floor(remainingSeconds.value / SECONDS_PER_DAY)));

const hours = computed(() => formatTimeUnit(Math.floor((remainingSeconds.value % SECONDS_PER_DAY) / SECONDS_PER_HOUR)));

const minutes = computed(() =>
  formatTimeUnit(Math.floor((remainingSeconds.value % SECONDS_PER_HOUR) / SECONDS_PER_MINUTE)),
);

const seconds = computed(() => formatTimeUnit(remainingSeconds.value % SECONDS_PER_MINUTE));
</script>

<style>
:host {
  display: block;
}
</style>

<style scoped lang="scss">
.sp-countdown {
  --countdown-font: var(--sp-sys-typescale-body-bold-short, inherit);
  --countdown-label-font-size: var(--sp-ref-spacing-20, 2.5rem);
  --countdown-label-font-size-mobile: var(--sp-ref-spacing-15, 1.875rem);
  --countdown-clock-font-size: var(--sp-ref-spacing-30, 3.75rem);
  --countdown-clock-font-size-mobile: var(--sp-ref-spacing-15, 1.875rem);
  --countdown-clock-label-font-size: var(--sp-ref-spacing-10, 1.25rem);
  --countdown-clock-label-font-size-mobile: var(--sp-ref-spacing-8, 1rem);
  --countdown-gap: var(--sp-ref-spacing-3, 0.375rem);
  --countdown-clock-margin: var(--sp-ref-spacing-8, 1rem);

  /* Stacked in column, until other variants are designed */
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: var(--countdown-gap);

  font: var(--countdown-font);

  &.--is-sm-screen {
    .sp-countdown__label {
      font-size: var(--countdown-label-font-size-mobile);
    }

    .sp-countdown__clock {
      font-size: var(--countdown-clock-font-size-mobile);
    }

    .sp-countdown__clock-label {
      font-size: var(--countdown-clock-label-font-size-mobile);
    }

    /* Hide selected text and add dot */
    .sp-countdown__clock-label span span {
      display: none;
    }

    .sp-countdown__clock-label span:has(span)::after {
      content: ".";
    }
  }
}

.sp-countdown__clock-container {
  display: flex;
  flex-direction: column;
}

.sp-countdown__label {
  font-size: var(--countdown-label-font-size);
}

.sp-countdown__clock {
  font-size: var(--countdown-clock-font-size);
}

.sp-countdown__clock-label {
  display: flex;
  font-size: var(--countdown-clock-label-font-size);
  gap: var(--countdown-gap);
  margin-inline: calc(var(--countdown-clock-margin) * -1);
}

.sp-countdown__clock-label > span {
  flex: 1;
  text-align: center;
}
</style>
